/**
 * ManuscriptReviewReportConfig
 * TypeScript types and configuration for the Manuscript Review Report app
 */

import { ReportConfig } from '../../../services/baseReport/types';
import { BaseReportData } from '../../../services/baseReport/types';

// ─────────────────────────────────────────────────────────────────────────────
// Paragraph-level types
// ─────────────────────────────────────────────────────────────────────────────

export type ParagraphFlag =
  | 'slow'
  | 'confusing'
  | 'contradiction'
  | 'info_dump'
  | 'head_hop'
  | 'cliche'
  | 'purple_prose'
  | 'weak_dialogue'
  | 'spelling'
  | 'grammar'
  | 'telling_not_showing'
  | 'passive_voice'
  | 'repetition'
  | 'adverb_overuse'
  | 'on_the_nose';

export type FlagSeverity = 'critical' | 'high' | 'medium' | 'style';

export const FLAG_SEVERITY: Record<ParagraphFlag, FlagSeverity> = {
  spelling:            'critical',
  grammar:             'critical',
  contradiction:       'high',
  head_hop:            'high',
  telling_not_showing: 'high',
  passive_voice:       'high',
  confusing:           'medium',
  slow:                'medium',
  info_dump:           'medium',
  repetition:          'medium',
  adverb_overuse:      'medium',
  on_the_nose:         'medium',
  weak_dialogue:       'medium',
  cliche:              'style',
  purple_prose:        'style',
};

export const SEVERITY_ORDER: Record<FlagSeverity, number> = {
  critical: 0,
  high:     1,
  medium:   2,
  style:    3,
};

export function topFlagSeverity(flags: ParagraphFlag[]): FlagSeverity | null {
  if (flags.length === 0) return null;
  return flags.reduce<FlagSeverity>((best, flag) => {
    const s = FLAG_SEVERITY[flag];
    return SEVERITY_ORDER[s] < SEVERITY_ORDER[best] ? s : best;
  }, 'style');
}

export type EmotionalTone =
  | 'tense'
  | 'hopeful'
  | 'sad'
  | 'joyful'
  | 'neutral'
  | 'action'
  | 'romantic'
  | 'melancholic'
  | 'humorous';

export type CategoryTag =
  | 'action'
  | 'dialogue'
  | 'backstory'
  | 'description'
  | 'introspection'
  | 'exposition'
  | 'transition';

export interface ParagraphReview {
  paragraph_id: string;
  paragraph_index: number;
  text_preview: string;
  pacing: number | null;
  engagement: number | null;
  clarity: number | null;
  prose_quality: number | null;
  dialogue_quality: number | null;
  emotional_impact: number | null;
  flags: ParagraphFlag[];
  comment: string | null;
  emotional_tone: EmotionalTone | null;
  category_tags: CategoryTag[];
}

// ─────────────────────────────────────────────────────────────────────────────
// Aggregate metrics
// ─────────────────────────────────────────────────────────────────────────────

export interface ManuscriptAggregateMetrics {
  overall_score: number | null;
  pacing_score: number | null;
  engagement_score: number | null;
  clarity_score: number | null;
  prose_quality_score: number | null;
  dialogue_score: number | null;
  emotional_impact_score: number | null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Full report data (mirrors ManuscriptReviewReportSkeleton on backend)
// ─────────────────────────────────────────────────────────────────────────────

export interface ManuscriptReviewReportData extends BaseReportData {
  manuscript_doc_id: string;
  manuscript_name: string;
  reviewer_id: string;
  reviewer_name: string;
  reviewed_at: string | null;
  // Aggregate scores
  overall_score: number | null;
  pacing_score: number | null;
  engagement_score: number | null;
  clarity_score: number | null;
  prose_quality_score: number | null;
  dialogue_score: number | null;
  emotional_impact_score: number | null;
  // Tone and flag summary
  dominant_tone: string | null;
  flag_frequency: Record<string, number> | null;
  tag_frequency: Record<string, number> | null;
  // Qualitative
  overall_assessment: string | null;
  strengths: string[];
  weaknesses: string[];
  recommended_changes: string[];
  // Stats
  total_paragraphs: number;
  paragraphs_reviewed: number;
  word_count: number;
  // Background job
  job_id?: string;
  // Per-paragraph
  paragraph_reviews: ParagraphReview[];
}

// ─────────────────────────────────────────────────────────────────────────────
// Report configuration
// ─────────────────────────────────────────────────────────────────────────────

export const MANUSCRIPT_REVIEW_REPORT_CONFIG: ReportConfig = {
  reportType: 'manuscript-review-report',
  displayName: 'Manuscript Review Report',
  pollIntervalMs: 5000,
};

// ─────────────────────────────────────────────────────────────────────────────
// Metric display metadata (label, description for each score field)
// ─────────────────────────────────────────────────────────────────────────────

export interface MetricMeta {
  key: keyof ManuscriptAggregateMetrics;
  label: string;
  shortLabel: string;
  description: string;
}

export const AGGREGATE_METRICS: MetricMeta[] = [
  {
    key: 'overall_score',
    label: 'Overall',
    shortLabel: 'Overall',
    description: 'Average of all six metrics',
  },
  {
    key: 'pacing_score',
    label: 'Pacing',
    shortLabel: 'Pacing',
    description: 'Story rhythm, tension, and scene momentum',
  },
  {
    key: 'engagement_score',
    label: 'Engagement',
    shortLabel: 'Engmt.',
    description: 'Reader interest and page-turn compulsion',
  },
  {
    key: 'clarity_score',
    label: 'Clarity',
    shortLabel: 'Clarity',
    description: 'How easy each paragraph is to follow',
  },
  {
    key: 'prose_quality_score',
    label: 'Prose Quality',
    shortLabel: 'Prose',
    description: 'Word choice, sentence flow, and writing craft',
  },
  {
    key: 'dialogue_score',
    label: 'Dialogue',
    shortLabel: 'Dialo.',
    description: 'Naturalness and voice distinction in dialogue',
  },
  {
    key: 'emotional_impact_score',
    label: 'Emotional Impact',
    shortLabel: 'Emot.',
    description: 'Emotional resonance and narrative payoff',
  },
];

export const FLAG_LABELS: Record<ParagraphFlag, string> = {
  spelling:            'Spelling',
  grammar:             'Grammar',
  telling_not_showing: 'Tell→Show',
  passive_voice:       'Passive Voice',
  contradiction:       'Contradiction',
  head_hop:            'Head Hop',
  confusing:           'Confusing',
  slow:                'Slow',
  info_dump:           'Info Dump',
  repetition:          'Repetition',
  adverb_overuse:      'Adverbs',
  on_the_nose:         'On the Nose',
  weak_dialogue:       'Weak Dialogue',
  cliche:              'Cliché',
  purple_prose:        'Purple Prose',
};

export const TONE_LABELS: Record<EmotionalTone, string> = {
  tense: 'Tense',
  hopeful: 'Hopeful',
  sad: 'Sad',
  joyful: 'Joyful',
  neutral: 'Neutral',
  action: 'Action',
  romantic: 'Romantic',
  melancholic: 'Melancholic',
  humorous: 'Humorous',
};
